import { Modal } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronLeft, FaDownload, FaEnvelope } from "react-icons/fa";
import edit from "shared/assets/icons/generate.svg";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import SelectAsync from "shared/components/Select/Async";
import { NAME_SPACES } from "shared/locales/constants";
import { ORGANIZATIONS } from "utils/api/graphql/queries/organizations";
import { API_DATE_TIME_FORMAT, DATE_FORMAT } from "utils/constants";
import "./ExportModal.scss";

const ExportModal = ({
  visible,
  onCancel,
  onConfirm,
  onSendByEmail,
  exportLoading,
  sendByEmailLoading,
  baseWhere = {},
  requiredOrganization = false,
  organizationFilter = {},
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const EXPORT_MODAL = t("LIST.HEADER.EXPORT_MODAL", { returnObjects: true });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startDateValue, setStartDateValue] = useState(null);
  const [endDateValue, setEndDateValue] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [dateError, setDateError] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedOrganizationName, setSelectedOrganizationName] =
    useState(null);
  const [organizationError, setOrganizationError] = useState(null);

  useEffect(() => {
    if (visible) {
      setIsExporting(false);
      setStartDate(null);
      setEndDate(null);
      setStartDateValue(null);
      setEndDateValue(null);
      setDateError(null);
      setSelectedOrganization(null);
      setSelectedOrganizationName(null);
      setOrganizationError(null);
    }
  }, [visible]);

  // Validation des dates
  useEffect(() => {
    if (startDate && endDate) {
      const start = dayjs(startDate);
      const end = dayjs(endDate);
      if (end.isBefore(start, "day")) {
        setDateError("DATE_END_BEFORE_START");
      } else {
        setDateError(null);
      }
    } else {
      setDateError(null);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (isExporting && !exportLoading && !sendByEmailLoading) {
      // Exportation terminée, fermer le modal après un court délai
      const timer = setTimeout(() => {
        setIsExporting(false);
        onCancel();
      }, 1000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportLoading, sendByEmailLoading, isExporting]);

  const buildVariables = () => {
    const variables = {
      where: {
        ...baseWhere,
      },
    };
    if (requiredOrganization && selectedOrganization) {
      variables.where.AND = [
        {
          project: {
            ownedBy: {
              id: selectedOrganization,
            },
          },
        },
      ];
    }

    if (startDate) {
      variables.gt = {
        createdDate: dayjs(startDate)
          .startOf("day")
          .format(API_DATE_TIME_FORMAT),
      };
    }

    if (endDate) {
      variables.lt = {
        createdDate: dayjs(endDate).endOf("day").format(API_DATE_TIME_FORMAT),
      };
    }
    return variables;
  };

  const handleConfirm = () => {
    if (dateError) {
      return;
    }
    if (requiredOrganization && !selectedOrganization) {
      setOrganizationError(
        t("LIST.HEADER.EXPORT_MODAL.ORGANIZATION_REQUIRED") ||
          "L'organisation est obligatoire",
      );
      return;
    }
    setIsExporting(true);
    const variables = buildVariables();
    onConfirm(variables, selectedOrganizationName);
  };

  const handleSendByEmail = () => {
    if (dateError) {
      return;
    }
    if (requiredOrganization && !selectedOrganization) {
      setOrganizationError(
        t("LIST.HEADER.EXPORT_MODAL.ORGANIZATION_REQUIRED") ||
          "L'organisation est obligatoire",
      );
      return;
    }
    // Fermer le modal immédiatement et lancer l'envoi en arrière-plan
    onCancel();
    const variables = buildVariables();
    onSendByEmail(variables, selectedOrganizationName);
  };

  if (isExporting) {
    return (
      <Modal
        className="modal__container"
        open={visible}
        onCancel={null}
        centered
        footer={null}
      >
        <div className="modal__container--body">
          <div className="export-modal__loading-container">
            <img
              src={edit}
              alt={"en cours"}
              className="export-modal__loading-icon"
            />
            <p className="export-modal__loading-text">
              {EXPORT_MODAL?.EXPORTING}
            </p>
          </div>
        </div>
      </Modal>
    );
  }

  const isConfirmDisabled =
    !!dateError || (requiredOrganization && !selectedOrganization);

  return (
    <Modal
      className="modal__container"
      open={visible}
      onCancel={onCancel}
      centered
      footer={[
        <Button
          key="cancel"
          type="primary--link"
          onClick={onCancel}
          icon={<FaChevronLeft />}
        >
          {EXPORT_MODAL?.CANCEL}
        </Button>,
        <Button
          key="download"
          type="primary--light"
          onClick={handleConfirm}
          icon={<FaDownload />}
          disabled={isConfirmDisabled}
          loading={exportLoading}
        >
          {EXPORT_MODAL?.DOWNLOAD || "Télécharger"}
        </Button>,
        <Button
          key="send"
          type="primary"
          onClick={handleSendByEmail}
          icon={<FaEnvelope />}
          disabled={isConfirmDisabled}
          loading={sendByEmailLoading}
        >
          {EXPORT_MODAL?.SEND_BY_EMAIL || "Envoyer par email"}
        </Button>,
      ]}
    >
      <div className="modal__container--body">
        <h3>{EXPORT_MODAL?.TITLE}</h3>
        <div className="export-modal__content">
          <p className="export-modal__message">{EXPORT_MODAL?.MESSAGE}</p>
          {requiredOrganization && (
            <div className="export-modal__field">
              <SelectAsync
                label={
                  t("LIST.HEADER.EXPORT_MODAL.ORGANIZATION_LABEL") ||
                  "Organisation"
                }
                node={{
                  query: ORGANIZATIONS,
                  variables: {
                    where: {
                      active: true,
                      ...organizationFilter,
                    },
                    take: 1000,
                  },
                  accessors: {
                    root: "organizations.data",
                    label: "name",
                    value: "id",
                  },
                }}
                value={selectedOrganization}
                onChange={(value, item) => {
                  setSelectedOrganization(value);
                  setSelectedOrganizationName(item?.name || null);
                  setOrganizationError(null);
                }}
                required={requiredOrganization}
                errors={organizationError}
                touched={!!organizationError}
                placeholder={
                  t("LIST.HEADER.EXPORT_MODAL.ORGANIZATION_PLACEHOLDER") ||
                  "Sélectionner une organisation"
                }
              />
            </div>
          )}
          <div className="export-modal__field">
            <Input
              type="date"
              label={EXPORT_MODAL?.START_DATE}
              placeholder={EXPORT_MODAL?.START_DATE_PLACEHOLDER}
              value={startDateValue}
              onChange={(date) => {
                setStartDateValue(date);
                setStartDate(
                  date ? (dayjs.isDayjs(date) ? date.toDate() : date) : null,
                );
                // Réinitialiser la date de fin si elle devient invalide
                if (
                  endDate &&
                  date &&
                  dayjs(endDate).isBefore(dayjs(date), "day")
                ) {
                  setEndDateValue(null);
                  setEndDate(null);
                }
              }}
              format={DATE_FORMAT}
              disabledDate={(current) => {
                // Désactiver les dates après la date de fin si elle est définie
                if (endDate && current) {
                  return (
                    current && dayjs(current).isAfter(dayjs(endDate), "day")
                  );
                }
                return false;
              }}
              errors={
                dateError === "DATE_END_BEFORE_START"
                  ? EXPORT_MODAL?.DATE_ERROR
                  : undefined
              }
              touched={!!dateError}
            />
          </div>
          <div className="export-modal__field">
            <Input
              type="date"
              label={EXPORT_MODAL?.END_DATE}
              placeholder={EXPORT_MODAL?.END_DATE_PLACEHOLDER}
              value={endDateValue}
              onChange={(date) => {
                setEndDateValue(date);
                setEndDate(
                  date ? (dayjs.isDayjs(date) ? date.toDate() : date) : null,
                );
              }}
              format={DATE_FORMAT}
              disabledDate={(current) => {
                // Désactiver les dates avant la date de début si elle est définie
                if (startDate && current) {
                  return (
                    current && dayjs(current).isBefore(dayjs(startDate), "day")
                  );
                }
                return false;
              }}
              errors={
                dateError === "DATE_END_BEFORE_START"
                  ? EXPORT_MODAL?.DATE_ERROR
                  : undefined
              }
              touched={!!dateError}
            />
          </div>
          {dateError && (
            <div className="export-modal__error">
              {EXPORT_MODAL?.DATE_ERROR}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ExportModal;
